import { useState } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FcrDeviceStatus } from '../types/models'
import { CalibrationDialog } from './CalibrationDialog'

function indicatorColor(device: FcrDeviceStatus): string {
  const state = device.commissioning_state
  const errcode = device.commissioning_errcode

  // If error code is active, color by errcode
  if (errcode && errcode > 0) {
    return errcode >= 100 ? '#ffa726' : '#f44336'
  }

  // Color by commissioning state
  if (state === null || state === undefined) return '#9e9e9e'
  if (state === 0) return '#f44336'
  if (state >= 1 && state <= 98) return '#ffa726'
  if (state === 99) return '#1565c0'
  return '#26a69a'
}

function indicatorValue(device: FcrDeviceStatus): string {
  const errcode = device.commissioning_errcode
  if (errcode && errcode > 0) return `E${errcode}`
  const state = device.commissioning_state
  if (state === null || state === undefined) return '—'
  if (state === 100) return 'OK'
  if (state === 99) return 'WAIT'
  return `${state}`
}

interface FcrStatusIndicatorProps {
  devices: FcrDeviceStatus[]
  userEmail: string
}

export function FcrStatusIndicator({ devices, userEmail }: FcrStatusIndicatorProps) {
  const { t } = useTranslation()
  const [dialogDevice, setDialogDevice] = useState<FcrDeviceStatus | null>(null)

  if (!devices || devices.length === 0) return null

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {devices.map((device) => {
          const errcode = device.commissioning_errcode
          const hasError = errcode && errcode > 0
          const tooltipText = hasError
            ? t(`Commissioning-Error-${errcode}`)
            : device.commissioning_state !== null && device.commissioning_state !== undefined
              ? t(`Commissioning-State-${device.commissioning_state}`)
              : ''

          return (
            <Tooltip key={device.hw_id} title={tooltipText} arrow>
              <Typography
                onClick={(e) => { e.stopPropagation(); setDialogDevice(device) }}
                sx={{
                  color: indicatorColor(device),
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  lineHeight: 1.3,
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {indicatorValue(device)}
              </Typography>
            </Tooltip>
          )
        })}
      </Box>
      {dialogDevice && (
        <CalibrationDialog
          open={!!dialogDevice}
          onClose={() => setDialogDevice(null)}
          nominalPower={dialogDevice.nominal_power || 0}
          hwId={dialogDevice.hw_id}
          userEmail={userEmail}
        />
      )}
    </>
  )
}
